import { useMemo } from 'react';
import { getProductionRssUrl } from '@/lib/api';
import { Modal, CopyButton } from '@/components/ui';

export interface RssInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEED_VARIABLES = [
  { key: 'body', label: 'Body' },
  { key: 'submitter', label: 'Submitter' },
  { key: 'location', label: 'Location' },
] as const;

const COMBINED_VARIABLES = [
  ...FEED_VARIABLES,
  { key: 'type', label: 'Type' },
] as const;

const FEED_GROUPS = [
  {
    title: 'Prayer Requests',
    basePath: 'live-prayer-request',
    variables: FEED_VARIABLES,
  },
  {
    title: 'Answered Prayers',
    basePath: 'live-praise-report',
    variables: FEED_VARIABLES,
  },
  {
    title: 'Combined',
    basePath: 'live-combined',
    variables: COMBINED_VARIABLES,
  },
] as const;

function RssFeedRow({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <div className="min-w-[5.5rem] shrink-0">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
        </span>
      </div>
      <div className="flex flex-1 gap-2 items-center min-w-0">
        <input
          type="text"
          readOnly
          value={url}
          aria-label={`${label} RSS feed URL`}
          className="block w-full min-h-[36px] rounded-lg border bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-600 px-3 py-2 text-sm font-mono text-slate-900 dark:text-white truncate"
        />
        <CopyButton text={url} size="sm" className="shrink-0" showLabel={false} label={`Copy ${label}`} />
      </div>
    </div>
  );
}

/**
 * Modal listing absolute RSS feed URLs for ProPresenter (body, submitter, location)
 * for prayer requests, answered prayers, and the combined mix (plus type).
 */
export function RssInfoModal({ isOpen, onClose }: RssInfoModalProps) {
  const groups = useMemo(
    () =>
      FEED_GROUPS.map((group) => ({
        title: group.title,
        feeds: group.variables.map((variable) => ({
          label: variable.label,
          url: getProductionRssUrl(`${group.basePath}/${variable.key}`),
        })),
      })),
    []
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="RSS Info" size="lg">
      <div className="space-y-6">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Copy these feed URLs into ProPresenter (or another display tool). Each feed updates when the producer sets a live item.
          The Combined type feed returns &quot;Prayer Request&quot; or &quot;Answered Prayer&quot;.
        </p>
        {groups.map((group) => (
          <div key={group.title} className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              {group.title}
            </h3>
            <div className="space-y-2">
              {group.feeds.map((feed) => (
                <RssFeedRow key={feed.label} label={feed.label} url={feed.url} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
